import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, FileText, Image as ImageIcon, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import AdminStatusStrip from '../components/AdminStatusStrip';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RISK_COLOR = {
  low: '#34D399',
  medium: '#F5C542',
  high: '#FF8C42',
  critical: '#FF5C5C',
};

const RISK_LABEL_PL = {
  low: 'Niskie ryzyko',
  medium: 'Średnie ryzyko',
  high: 'Wysoki priorytet weryfikacji',
  critical: 'Wysoki priorytet weryfikacji (krytyczny)',
};

const CONFIDENCE_LABEL_PL = {
  low: 'niska',
  medium: 'średnia',
  high: 'wysoka',
};

const DISCLAIMER_PL = 'Wynik jest oceną probabilistyczną, nie ostatecznym dowodem. Wymaga weryfikacji przez człowieka.';

const ResultCard = ({ result, onMarkReviewed }) => {
  if (!result) return null;
  return (
    <Card
      data-testid="authenticity-result-card"
      style={{
        padding: 20,
        marginTop: 18,
        borderLeft: `4px solid ${RISK_COLOR[result.risk_level] || '#6B7280'}`,
        background: 'rgba(255,255,255,0.03)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{
            fontSize: 10, letterSpacing: 1, fontWeight: 700,
            textTransform: 'uppercase', color: RISK_COLOR[result.risk_level],
          }}>
            {RISK_LABEL_PL[result.risk_level] || result.risk_level}
          </div>
          <h3 style={{ marginTop: 4, fontSize: 18, fontWeight: 700 }} data-testid="authenticity-result-summary-pl">
            {result.summary_pl}
          </h3>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Pewność: {CONFIDENCE_LABEL_PL[result.confidence] || result.confidence}</div>
          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>{result.created_at}</div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12, marginTop: 16,
      }}>
        <Card style={{ padding: 12, background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>AI likelihood</div>
          <div style={{ fontSize: 28, fontWeight: 700 }} data-testid="authenticity-result-ai-percent">
            {result.ai_likelihood_percent}%
          </div>
        </Card>
        <Card style={{ padding: 12, background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Manipulation likelihood</div>
          <div style={{ fontSize: 28, fontWeight: 700 }} data-testid="authenticity-result-manip-percent">
            {result.manipulation_likelihood_percent}%
          </div>
        </Card>
      </div>

      {result.signals && result.signals.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Sygnały</div>
          <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, lineHeight: 1.55 }} data-testid="authenticity-result-signals">
            {result.signals.slice(0, 8).map((s) => (
              <li key={s.name}><strong>{s.name}</strong> ({s.weight}) — {s.finding}</li>
            ))}
          </ul>
        </div>
      )}

      {result.recommended_next_steps && result.recommended_next_steps.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Rekomendowane kroki weryfikacji</div>
          <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13 }} data-testid="authenticity-result-next-steps">
            {result.recommended_next_steps.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      )}

      {result.limitations && result.limitations.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Ograniczenia</div>
          <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, opacity: 0.85 }} data-testid="authenticity-result-limitations">
            {result.limitations.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      )}

      <div
        data-testid="authenticity-result-disclaimer"
        style={{
          marginTop: 18, padding: 12, borderRadius: 6,
          background: 'rgba(245,158,11,0.08)',
          borderLeft: '3px solid #F59E0B',
          fontSize: 12, opacity: 0.95,
        }}
      >
        <strong>{result.disclaimer_pl}</strong>
        <div style={{ marginTop: 4, opacity: 0.8 }}>{result.disclaimer_en}</div>
      </div>

      {result.human_review_required && result.review_status === 'pending' && (
        <Button
          onClick={() => onMarkReviewed(result.id)}
          data-testid={`authenticity-mark-reviewed-${result.id}`}
          style={{ marginTop: 14 }}
        >
          <CheckCircle2 className="w-4 h-4 mr-2" /> Oznacz jako zweryfikowane
        </Button>
      )}
    </Card>
  );
};

const AuthenticityShield = () => {
  const [activeTab, setActiveTab] = useState('text');
  const [text, setText] = useState('');
  const [sourceContext, setSourceContext] = useState('');
  const [claimedAuthor, setClaimedAuthor] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageSourceContext, setImageSourceContext] = useState('');
  const [imageClaimedOrigin, setImageClaimedOrigin] = useState('');
  const [latestResult, setLatestResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const [results, setResults] = useState([]);
  const [filterContentType, setFilterContentType] = useState('all');
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');
  const [filterReviewStatus, setFilterReviewStatus] = useState('all');
  const [filterQ, setFilterQ] = useState('');
  const [loadingList, setLoadingList] = useState(false);

  const listParams = useMemo(() => {
    const p = { limit: 30 };
    if (filterContentType !== 'all') p.content_type = filterContentType;
    if (filterRiskLevel !== 'all') p.risk_level = filterRiskLevel;
    if (filterReviewStatus !== 'all') p.review_status = filterReviewStatus;
    if (filterQ.trim()) p.q = filterQ.trim();
    return p;
  }, [filterContentType, filterRiskLevel, filterReviewStatus, filterQ]);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await axios.get(`${API}/admin/authenticity/results`, {
        withCredentials: true, params: listParams,
      });
      setResults(r.data?.results || []);
    } catch (e) {
      // Silent on the list — the page should still allow new analyses.
    } finally {
      setLoadingList(false);
    }
  }, [listParams]);

  useEffect(() => { refreshList(); }, [refreshList]);

  const submitText = async () => {
    setSubmitting(true);
    setErr('');
    try {
      const r = await axios.post(`${API}/admin/authenticity/text`, {
        text,
        source_context: sourceContext || null,
        claimed_author: claimedAuthor || null,
      }, { withCredentials: true });
      setLatestResult(r.data);
      refreshList();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Błąd analizy tekstu');
    } finally {
      setSubmitting(false);
    }
  };

  const submitImage = async () => {
    if (!imageFile) { setErr('Wybierz plik obrazu'); return; }
    setSubmitting(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('image', imageFile);
      if (imageSourceContext) fd.append('source_context', imageSourceContext);
      if (imageClaimedOrigin) fd.append('claimed_origin', imageClaimedOrigin);
      const r = await axios.post(`${API}/admin/authenticity/image`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLatestResult(r.data);
      refreshList();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Błąd analizy obrazu');
    } finally {
      setSubmitting(false);
    }
  };

  const markReviewed = async (id) => {
    try {
      await axios.post(`${API}/admin/authenticity/results/${id}/mark-reviewed`,
        { status: 'reviewed' }, { withCredentials: true });
      refreshList();
      if (latestResult?.id === id) {
        setLatestResult({ ...latestResult, review_status: 'reviewed' });
      }
    } catch (e) {
      setErr('Nie udało się oznaczyć jako zweryfikowane');
    }
  };

  return (
    <div className="hussar-landing" data-testid="admin-authenticity-shield-page" style={{ minHeight: '100vh' }}>
      <div className="hussar-container" style={{ paddingTop: 32, paddingBottom: 80 }}>
        <AdminStatusStrip />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <Shield className="w-6 h-6" style={{ color: '#7DD3FC' }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }} data-testid="authenticity-h1">
            Tarcza Autentyczności
          </h1>
        </div>
        <p style={{ marginTop: 8, opacity: 0.75, fontSize: 14, maxWidth: 760 }}>
          Analiza probabilistyczna tekstów i obrazów. <strong>Nigdy nie ostateczny dowód.</strong> Każdy wynik wymaga weryfikacji przez człowieka. Bez rozpoznawania osób.
        </p>

        {/* Tab switch */}
        <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
          <Button
            variant={activeTab === 'text' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('text'); setLatestResult(null); setErr(''); }}
            data-testid="authenticity-tab-text"
          >
            <FileText className="w-4 h-4 mr-2" /> Tekst
          </Button>
          <Button
            variant={activeTab === 'image' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('image'); setLatestResult(null); setErr(''); }}
            data-testid="authenticity-tab-image"
          >
            <ImageIcon className="w-4 h-4 mr-2" /> Obraz
          </Button>
        </div>

        {/* Forms */}
        {activeTab === 'text' && (
          <Card style={{ padding: 20, marginTop: 14 }}>
            <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>Tekst do analizy</label>
            <Textarea
              value={text} onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder="Wklej tekst, który chcesz przeanalizować…"
              data-testid="authenticity-text-input"
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 12 }}>
              <Input
                value={sourceContext}
                onChange={(e) => setSourceContext(e.target.value)}
                placeholder="Kontekst źródła (opcjonalnie)"
                data-testid="authenticity-text-source-context"
              />
              <Input
                value={claimedAuthor}
                onChange={(e) => setClaimedAuthor(e.target.value)}
                placeholder="Deklarowany autor (opcjonalnie)"
                data-testid="authenticity-text-claimed-author"
              />
            </div>
            <Button
              onClick={submitText}
              disabled={submitting || text.trim().length === 0}
              style={{ marginTop: 14 }}
              data-testid="authenticity-submit-text"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizuję…</> : 'Analizuj tekst'}
            </Button>
          </Card>
        )}

        {activeTab === 'image' && (
          <Card style={{ padding: 20, marginTop: 14 }}>
            <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>Obraz do analizy (PNG / JPEG / WebP / GIF)</label>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              data-testid="authenticity-image-input"
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 12 }}>
              <Input
                value={imageSourceContext}
                onChange={(e) => setImageSourceContext(e.target.value)}
                placeholder="Kontekst (opcjonalnie)"
                data-testid="authenticity-image-source-context"
              />
              <Input
                value={imageClaimedOrigin}
                onChange={(e) => setImageClaimedOrigin(e.target.value)}
                placeholder="Deklarowane źródło (opcjonalnie)"
                data-testid="authenticity-image-claimed-origin"
              />
            </div>
            <Button
              onClick={submitImage}
              disabled={submitting || !imageFile}
              style={{ marginTop: 14 }}
              data-testid="authenticity-submit-image"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizuję…</> : 'Analizuj obraz'}
            </Button>
          </Card>
        )}

        {err && (
          <div data-testid="authenticity-error" style={{
            marginTop: 12, padding: 10, borderRadius: 6,
            background: 'rgba(239,68,68,0.1)', color: '#FF8888', fontSize: 13,
          }}>
            <AlertTriangle className="w-4 h-4 inline mr-1" /> {err}
          </div>
        )}

        <ResultCard result={latestResult} onMarkReviewed={markReviewed} />

        {/* Doctrine reminder */}
        <Card style={{
          marginTop: 26, padding: 14, borderLeft: '3px solid #7DD3FC',
          background: 'rgba(125,211,252,0.03)', fontSize: 12, opacity: 0.85,
        }}>
          <strong>{DISCLAIMER_PL}</strong> · System nie identyfikuje osób; nie wykonuje dopasowania biometrycznego.
        </Card>

        {/* Prior results list */}
        <div style={{ marginTop: 36 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }} data-testid="authenticity-history-h2">
            Wcześniejsze analizy
          </h2>

          <Card style={{
            padding: 14, marginTop: 12,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
          }}>
            <select
              value={filterContentType}
              onChange={(e) => setFilterContentType(e.target.value)}
              data-testid="authenticity-filter-content-type"
              style={{ padding: 8, borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <option value="all">all types</option>
              <option value="text">text</option>
              <option value="image">image</option>
            </select>
            <select
              value={filterRiskLevel}
              onChange={(e) => setFilterRiskLevel(e.target.value)}
              data-testid="authenticity-filter-risk"
              style={{ padding: 8, borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <option value="all">all risk</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
              <option value="critical">critical</option>
            </select>
            <select
              value={filterReviewStatus}
              onChange={(e) => setFilterReviewStatus(e.target.value)}
              data-testid="authenticity-filter-review-status"
              style={{ padding: 8, borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <option value="all">all reviews</option>
              <option value="pending">pending</option>
              <option value="reviewed">reviewed</option>
              <option value="dismissed">dismissed</option>
              <option value="not_required">not_required</option>
            </select>
            <Input
              value={filterQ}
              onChange={(e) => setFilterQ(e.target.value)}
              placeholder="search"
              data-testid="authenticity-filter-q"
            />
            <Button onClick={refreshList} data-testid="authenticity-refresh-list">
              <RefreshCw className="w-4 h-4 mr-2" /> Odśwież
            </Button>
          </Card>

          <div data-testid="authenticity-history-list" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loadingList && <span style={{ opacity: 0.7, fontSize: 13 }}><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Ładowanie…</span>}
            {!loadingList && results.length === 0 && (
              <Card data-testid="authenticity-history-empty" style={{ padding: 16, opacity: 0.7 }}>Brak wyników.</Card>
            )}
            {results.map((r) => (
              <Card
                key={r.id}
                data-testid={`authenticity-history-row-${r.id}`}
                style={{
                  padding: 12,
                  borderLeft: `3px solid ${RISK_COLOR[r.risk_level] || '#6B7280'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: RISK_COLOR[r.risk_level], textTransform: 'uppercase' }}>
                      {r.content_type} · {RISK_LABEL_PL[r.risk_level] || r.risk_level}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4, fontWeight: 600 }}>{r.summary_pl}</div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                      AI {r.ai_likelihood_percent}% · manip. {r.manipulation_likelihood_percent}% · pewność: {CONFIDENCE_LABEL_PL[r.confidence] || r.confidence} · status: {r.review_status}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>{r.created_at}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticityShield;
